import { Injectable } from '@angular/core';
import { CreatorStateService } from '../services/app-services';
import { SvgPolylineBuilder } from '../builders';
import { cloneDeep } from 'lodash';
import { GuidWrapper } from '../types';
import { IRankedSeats, ISeat } from '../interfaces';
import { SeatManager } from './seat.manager';
import { ShapeAttributesManager } from './shape-attributes.manager';
import { DragStates, RowSeatsOrders, ShapeKeys } from '../enums';

@Injectable()
export class RowManager {

  private static _lastElement: GuidWrapper<ISeat>;
  private static _textOffsetX = 65;
  private static _textOffsetY = 5;
  private static readonly _svgPolylineBuilder = new SvgPolylineBuilder();

  public static getRowSeats(rankToken: string): GuidWrapper<ISeat>[] {
    return CreatorStateService.seatsPlan.seats.filter(x => x.rankToken === rankToken);
  }

  public static getAllRanksWithTokens(seats?: GuidWrapper<ISeat>[]): { rank: number, rankToken: string }[] {
    const data = (seats ? seats : CreatorStateService.seatsPlan.seats).map(seat => ({
      rank: seat.rank,
      rankToken: seat.rankToken
    }));

    const uniqueDataMap = new Map<string, { rank: number, rankToken: string }>();

    data.forEach(item => {
      if (item.rank !== 0 && !uniqueDataMap.has(item.rankToken)) {
        uniqueDataMap.set(item.rankToken, item);
      }
    });

    return Array.from(uniqueDataMap.values());
  }

  public static formatSeatsToSeatsSeatsWithRanks(seats: GuidWrapper<ISeat>[]): IRankedSeats[] {
    const seatGroups = seats.reduce((acc, seat) => {
      if (seat.rank === 0) {
        return acc;
      }

      if (!acc[seat.rankToken]) {
        acc[seat.rankToken] = [];
      }

      acc[seat.rankToken].push(seat);

      return acc;
    }, {} as Record<string, GuidWrapper<ISeat>[]>);

    return Object.entries(seatGroups).map(([rankToken, seats]) => ({
      rankToken,
      seats
    }));
  }

  public static getRowTokenByCoordinates(x: number, y: number): string {
    const rowNumbersEls = CreatorStateService.roomRef.nativeElement.querySelectorAll('svg.row-svg text');
    let closestEl = null;
    let minDistance = Infinity;

    rowNumbersEls.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const elX = rect.x + rect.width/2;
      const elY = rect.y + rect.height/2;
      const distance = Math.sqrt(Math.pow(elX - x, 2) + Math.pow(elY - y, 2));
      
      if (distance < minDistance && distance <= 25) {
        minDistance = distance;
        closestEl = el;
      }
    });

    return closestEl?.closest('svg')?.id ?? '';
  }

  public static updateRowLine(rankToken: string, seats?: GuidWrapper<ISeat>[], container?: HTMLElement): void {
    if (!rankToken || rankToken === '0') {
      return;
    }

    const svg = (container ?? CreatorStateService.roomRef.nativeElement).querySelector(`svg#${CSS.escape(rankToken)}`);
    let rowSeats = seats?.length ? seats : RowManager.getRowSeats(rankToken);

    if (!rowSeats.length) {
      svg?.remove();
      return;
    }

    rowSeats = RowManager.sortSeats(rowSeats);
    const rank = rowSeats[0]?.rank;

    if (!svg) {
      return RowManager.createSvgLine(rank, rankToken, rowSeats);
    } else {
      if (!rowSeats.length) {
        return svg.remove();
      }

      svg.querySelector('polyline').setAttribute('points', RowManager.getPoints(rowSeats));
      svg.querySelector('text').setAttribute('x', (this._lastElement.sectorElement.webX + this._lastElement.sectorElement.w + this._textOffsetX).toString());
      svg.querySelector('text').setAttribute('y', ((this._lastElement.sectorElement.webY + (this._lastElement.sectorElement.h / 2)) + this._textOffsetY).toString());
    }
  }

  public static createSvgLine(rank: number, rankToken: string, seats: GuidWrapper<ISeat>[], container?: HTMLElement): void {
    const svg = this._svgPolylineBuilder
      .withPolyline(this.getPoints(seats))
      .withText(
        (this._lastElement.sectorElement.webX + this._lastElement.sectorElement.w + this._textOffsetX).toString(),
        (this._lastElement.sectorElement.webY + (this._lastElement.sectorElement.h / 2) + this._textOffsetY).toString(),
        `${rank} (${rankToken})`
      )
      .build(rankToken);

    (container ?? CreatorStateService.roomRef.nativeElement).appendChild(svg);
  }

  public static renumberSeatsInRow(
    rankToken: string,
    direction?: 'horizontal' | 'vertical',
    order: RowSeatsOrders = RowSeatsOrders.LEFT_TO_RIGHT
  ): GuidWrapper<ISeat>[] {
    return this.renumberSeats(
      RowManager.getRowSeats(rankToken),
      1,
      direction,
      order
    );
  }

  public static getRowDirectionAndOrder(rankToken: string): { direction: 'horizontal' | 'vertical', order: RowSeatsOrders } {
    const rowSeats = RowManager.getRowSeats(rankToken).sort((a, b) => a.sectorElement.webX - b.sectorElement.webX);

    if (!rowSeats.length) {
      return {
        direction: 'horizontal',
        order: RowSeatsOrders.LEFT_TO_RIGHT
      };
    }

    const firstSeat = rowSeats[0];
    const lastSeat = rowSeats[rowSeats.length - 1];

    const horizontalDistance = Math.abs(lastSeat.sectorElement.webX - firstSeat.sectorElement.webX);
    const verticalDistance = Math.abs(lastSeat.sectorElement.webY - firstSeat.sectorElement.webY);

    const direction = horizontalDistance > verticalDistance ? 'horizontal' : 'vertical';
    let order: RowSeatsOrders;

    if (direction === 'horizontal') {
      order = firstSeat.sectorElement.webX > lastSeat.sectorElement.webX ? 
        RowSeatsOrders.RIGHT_TO_LEFT : 
        RowSeatsOrders.LEFT_TO_RIGHT;
    } else {
      order = firstSeat.sectorElement.webY > lastSeat.sectorElement.webY ?
        RowSeatsOrders.RIGHT_TO_LEFT :
        RowSeatsOrders.LEFT_TO_RIGHT;
    }

    return { direction, order };
  }

  public static renumberSeats(
    seats: GuidWrapper<ISeat>[],
    startNumber: number = 1,
    direction?: 'horizontal' | 'vertical',
    order: RowSeatsOrders = RowSeatsOrders.LEFT_TO_RIGHT
  ): GuidWrapper<ISeat>[] {
    const sortedSeats: GuidWrapper<ISeat>[] = [];

    if (direction === 'vertical') {
      sortedSeats.push(...seats.sort((a, b) => a.sectorElement.webY - b.sectorElement.webY));
    } else if (direction === 'horizontal') {
      sortedSeats.push(...this.sortSeats(seats));
    } else {
      sortedSeats.push(
        ...this.sortSeats(seats).sort((a, b) => a.seatNumber - b.seatNumber)
      );
    }

    if (order === RowSeatsOrders.RIGHT_TO_LEFT) {
      sortedSeats.reverse();
    }

    let currentNumber = startNumber;

    for (let i = 0; i < sortedSeats.length; i++) {
      const seat = sortedSeats[i];

      if (!seat.sectorElement.flagSeatFac) {
        continue;
      }

      const fIndex = CreatorStateService.seatsPlan.seats.findIndex((s) => s.guid === seat.guid);
      seat.seatNumber = currentNumber++;
      CreatorStateService.seatsPlan.seats[fIndex] = seat;
    }

    return sortedSeats;
  }

  public static get currentRowRank(): number {
    return cloneDeep(CreatorStateService.seatsPlan.seats).reduce((maxRank: number, seat: GuidWrapper<ISeat>) => {
      return seat.rank > maxRank ? seat.rank : maxRank;
    }, 0);
  }

  public static getAvailableRankToken(rank: number): string {
    if (!rank) return null;

    const seatsWithRank = CreatorStateService.seatsPlan.seats.filter(seat => seat.rank === rank);

    if (seatsWithRank.length === 0) return `${rank}`;

    let maxTokenNumber = 0;

    for (const seat of seatsWithRank) {
      const rankTokenParts = seat.rankToken.split('.');
      const tokenNumber = parseInt(rankTokenParts[1], 10) ?? 0;

      if (tokenNumber > maxTokenNumber) {
        maxTokenNumber = tokenNumber;
      }
    }

    return `${rank}.${maxTokenNumber + 1}`;
  }

  public static createSelection(rankToken: string, container?: HTMLElement): HTMLElement {
    const seats = RowManager.getRowSeats(rankToken);

    if (!seats.length) {
      return null;
    }

    let topElement = seats[0];
    let bottomElement = seats[0];
    let leftElement = seats[0];
    let rightElement = seats[0];

    for (const seat of seats) {
      if (seat.sectorElement.webY < topElement.sectorElement.webY) {
        topElement = seat;
      }
      if ((seat.sectorElement.webY + seat.sectorElement.h) > (bottomElement.sectorElement.webY + bottomElement.sectorElement.h)) {
        bottomElement = seat;
      }
      if (seat.sectorElement.webX < leftElement.sectorElement.webX) {
        leftElement = seat;
      }
      if ((seat.sectorElement.webX + seat.sectorElement.w) > (rightElement.sectorElement.webX + rightElement.sectorElement.w)) {
        rightElement = seat;
      }

      const seatEl = SeatManager.getHTMLElement(seat.guid);

      if (seatEl) {
        ShapeAttributesManager.setDragState(seatEl, DragStates.SELECTED);
        ShapeAttributesManager.setActiveExternalSelection(seatEl, 1);
      }
    }

    const selection = document.createElement('div');
    selection.className = 'external-selection';
    selection.style.top = `${topElement.sectorElement.webY}px`;
    selection.style.left = `${leftElement.sectorElement.webX}px`;
    selection.style.width = `${rightElement.sectorElement.webX + rightElement.sectorElement.w - leftElement.sectorElement.webX}px`;
    selection.style.height = `${bottomElement.sectorElement.webY + bottomElement.sectorElement.h - topElement.sectorElement.webY}px`;

    (container ?? CreatorStateService.roomRef.nativeElement).appendChild(selection);
    return selection;
  }

  public static removeSelections(container?: HTMLElement): void {
    const selections = (container ?? CreatorStateService.roomRef.nativeElement).querySelectorAll('.external-selection');

    selections.forEach(x => x.remove());

    const seatsEls = (container ?? CreatorStateService.roomRef.nativeElement)
      .querySelectorAll<HTMLElement>(`app-shape[${ShapeAttributesManager.getAttribute(ShapeKeys.ACTIVE_EXTERNAL_SELECTION)}="1"]`);

    seatsEls.forEach(x => ShapeAttributesManager.setActiveExternalSelection(x, 0));
  }

  private static sortSeats(seats: GuidWrapper<ISeat>[]): GuidWrapper<ISeat>[] {
    return seats.sort((a, b) => a.sectorElement.webX - b.sectorElement.webX);
  }

  private static getPoints(seats: GuidWrapper<ISeat>[]): string {
    const sortedSeats = cloneDeep(this.sortSeats(seats));
    const points = seats
      .sort((a, b) => a.seatNumber - b.seatNumber)
      .map((seat) => `${seat.sectorElement.webX + seat.sectorElement.w / 2},${seat.sectorElement.webY + seat.sectorElement.h / 2}`);

    this._lastElement = sortedSeats[sortedSeats.length - 1];

    return points.join(' ');
  }
}
